import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import {
  ActionSheetController,
  AlertController,
  IonButton,
  IonNote,
  IonSpinner,
  LoadingController,
  ModalController,
  NavController,
  ToastController,
} from '@ionic/angular/standalone';
import { CommonRoute } from '../../../../../common/enums/common-route.enum';
import { DownloadCopy, offlineCapSentence } from '../../../../../common/enums/download-message.enum';
import { DeleteZoneMessage } from '../../../../../common/enums/delete-zone-message.enum';
import { LoadingMessage } from '../../../../../common/enums/loading-message.enum';
import { SyncMessage } from '../../../../../common/enums/sync-message.enum';
import { ToastLife } from '../../../../../common/enums/toast-life.enum';
import { LogFactory } from '../../../../../common/services/logger/log-factory';
import { NetworkService } from '../../../../../common/services/network/network.service';
import { CloudItemPreviewComponent } from '../cloud-item-preview/cloud-item-preview.component';
import {
  DeletableItem,
  DeletedItemsListComponent,
} from '../deleted-items-list/deleted-items-list.component';
import { ItemType } from '../../models/item-type';
import { DbService } from '../../services/db.service';
import { ItemDownloadService } from '../../services/item-download.service';
import { OfflineLibraryService } from '../../services/offline-library.service';
import { CloudItem } from '../../sync/api-sync.backend';
import { SyncService } from '../../sync/sync.service';

/** Which copies a delete on this screen takes. */
const enum CloudDeleteScope {
  CloudOnly = 'cloud-only',
  Everywhere = 'everywhere',
}
import { formatBytes } from '../../utils/format-bytes.util';
import { confirmMeteredTransfer } from './metered-confirm.util';

/** Rows per page in each type's section of the cloud list. Kept short so the
 *  list never pushes the Failed backups and wipe cards below it out of reach
 *  on a phone; a business library runs to hundreds of items. */
const PAGE_SIZE_BY_KIND: Record<ItemType, number> = {
  [ItemType.Activity]: 5,
  [ItemType.Group]: 5,
  [ItemType.Sequence]: 5,
  [ItemType.Program]: 5,
};

/**
 * "In the Cloud": everything the user is storing, what each item costs, and the
 * two things they can do about it — pull a selection back down to this device,
 * or delete it from the cloud for good.
 *
 * It lists EVERYTHING, not just recently-backed-up items. Cloud storage is paid
 * for by the byte, so a section whose job is freeing space has to show all of it.
 * All of it, a page at a time: each type's section pages and searches on its
 * own, on the client (one server response holds the lot), while the summary
 * card above keeps counting the whole library.
 *
 * It lives inside the Cloud backup sheet rather than on a page of its own
 * because every question it answers is asked while looking at that sheet:
 * what is up there, is there room for the next backup, and can I get this one
 * item back without overwriting everything else. The storage-full dialog's
 * "Free up space" scrolls here instead of leaving for another screen.
 *
 * The whole-backup wipe is NOT here — it sits below the Failed backups section
 * (see CloudWipeCardComponent), after everything it would destroy.
 *
 * The list is server-sourced: a wiped or second device holds none of the rows,
 * and only the server can price an item against the whole cloud library. That
 * means it needs a connection — hence the explicit loading/error states.
 */
@Component({
  selector: 'app-cloud-items-section',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonButton, IonNote, IonSpinner, DeletedItemsListComponent],
  template: `
    <div class="section-title" #anchor>
      <h2>{{ Title }}</h2>
      @if (list()?.selecting()) {
        <ion-button size="small" fill="clear" (click)="list()!.exitSelectMode()">
          {{ Cancel }}
        </ion-button>
      } @else if (items().length) {
        <ion-button size="small" fill="clear" (click)="list()!.enterSelectMode()" data-testid="ci-select">
          {{ Select }}
        </ion-button>
      }
    </div>

    <ion-note class="intro-note">{{ CloudIntro }}</ion-note>

    @if (loading()) {
      <div class="state-block">
        <ion-spinner name="crescent"></ion-spinner>
        <p class="muted">Loading…</p>
      </div>
    } @else if (loadError()) {
      <div class="state-block" data-testid="ci-load-error">
        <p class="muted">{{ CloudLoadFailed }}</p>
        <ion-button fill="outline" (click)="reload()">Try again</ion-button>
      </div>
    } @else {
      <app-deleted-items-list
        [items]="items()"
        [pageSizes]="PageSizes"
        [emptyText]="CloudEmpty"
        [showRestore]="true"
        [restoreLabel]="RestoreLabel"
        restoreIcon="cloud-download-outline"
        [openable]="true"
        [selectionReclaimBytes]="selectionReclaimBytes()"
        (openOne)="preview($event)"
        (deleteOne)="deleteOne($event)"
        (deleteMany)="deleteMany($event)"
        (restoreOne)="restoreOne($event)"
        (restoreMany)="restoreMany($event)"
        (selectionChange)="selection.set($event)"
      ></app-deleted-items-list>
    }

  `,
  styles: [`
    :host { display: block; }
    .section-title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      margin: 18px 0 8px;
    }
    .section-title h2 {
      font-size: 14px;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: var(--app-text-muted, rgba(0, 0, 0, 0.55));
      margin: 0;
    }
    .section-title ion-button { margin: 0; --padding-start: 6px; --padding-end: 6px; }
    .intro-note { display: block; margin-bottom: 12px; font-size: 13px; line-height: 1.4; }
    .state-block { text-align: center; padding: 32px 16px; }
    .muted { color: var(--ion-color-medium); font-size: 0.85rem; margin-bottom: 12px; }
    /* Brief highlight when "Free up space" scrolls the user here, so it is
       obvious which part of a long sheet just answered them. */
    :host(.revealed) .section-title h2 { color: var(--ion-color-primary); }
  `],
})
export class CloudItemsSectionComponent {
  private readonly sync = inject(SyncService);
  private readonly db = inject(DbService);
  private readonly alertCtrl = inject(AlertController);
  private readonly sheetCtrl = inject(ActionSheetController);
  private readonly loadingCtrl = inject(LoadingController);
  private readonly modalCtrl = inject(ModalController);
  private readonly toastCtrl = inject(ToastController);
  private readonly network = inject(NetworkService);
  private readonly downloads = inject(ItemDownloadService);
  private readonly offlineLibrary = inject(OfflineLibraryService);
  private readonly nav = inject(NavController);
  private readonly logger = inject(LogFactory).createLogger(CloudItemsSectionComponent);

  /** Optional: the list is not rendered while loading or errored. */
  protected readonly list = viewChild(DeletedItemsListComponent);
  /** Scroll target for the storage-full dialog's "Free up space". */
  readonly anchor = viewChild.required<ElementRef<HTMLElement>>('anchor');

  protected readonly Title = SyncMessage.CloudItemsTitle;
  protected readonly Select = SyncMessage.CloudItemsSelect;
  protected readonly Cancel = SyncMessage.Cancel;
  protected readonly RestoreLabel = SyncMessage.RestoreItemsButton;
  protected readonly CloudEmpty = DeleteZoneMessage.CloudEmpty;
  protected readonly CloudIntro = DeleteZoneMessage.CloudIntro;
  protected readonly CloudLoadFailed = DeleteZoneMessage.CloudLoadFailed;
  protected readonly PageSizes = PAGE_SIZE_BY_KIND;

  /** The shared snapshot, so this list and the summary above it are the same
   *  fetch rather than two. Empty while it has never loaded — the loading and
   *  error blocks below own that state. */
  protected readonly items = computed<CloudItem[]>(() => this.sync.cloudItems() ?? []);
  protected readonly loading = signal(true);
  protected readonly loadError = signal(false);
  protected readonly selection = signal<DeletableItem[]>([]);

  /**
   * What the current selection would free. A plain sum of each row's exclusive
   * bytes would be wrong whenever two selected items share media: neither row
   * claims those bytes on its own, yet deleting both does release them. The
   * server already accounted for sharing per item, so the honest local answer is
   * the sum of the exclusive figures plus nothing — and where rows share media
   * the true total is higher, never lower, so this never over-promises.
   */
  protected readonly selectionReclaimBytes = computed(() =>
    this.selection().reduce((sum, i) => sum + (i.exclusiveBytes ?? 0), 0),
  );

  constructor() {
    void this.reload();
  }

  /** Re-read the cloud list. Public so the sheet can refresh after a backup. */
  async reload(): Promise<void> {
    this.loading.set(true);
    this.loadError.set(false);
    try {
      await Promise.all([
        this.sync.listCloudItems(),
        this.sync.refreshStorageUsageQuietly(),
      ]);
    } catch (err) {
      this.logger.error('Loading Cloud items failed', err);
      this.loadError.set(true);
    } finally {
      this.loading.set(false);
    }
  }

  /** Row tap: show what the item is and contains before anything is destroyed. */
  protected async preview(item: DeletableItem): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: CloudItemPreviewComponent,
      componentProps: { kind: item.kind, itemId: item.id, summary: item },
    });
    await modal.present();
    const { data } = await modal.onDidDismiss<{ deleteRequested?: boolean }>();
    if (data?.deleteRequested) await this.deleteOne(item);
  }

  protected async deleteOne(item: DeletableItem): Promise<void> {
    const scope = await this.chooseScope([item]);
    if (!scope) return;
    await this.removeFromCloud([item], scope);
  }

  protected async deleteMany(items: DeletableItem[]): Promise<void> {
    if (!items.length) return;
    const scope = await this.chooseScope(items);
    if (!scope) return;
    await this.removeFromCloud(items, scope);
    this.list()?.exitSelectMode();
    this.selection.set([]);
  }

  /**
   * Cloud only, or everywhere?
   *
   * The choice is offered only when this device actually holds one of these
   * items, because otherwise "and from this device" would delete nothing and
   * say it did. Deleting from the Cloud alone is not a half-measure here: the
   * device copy survives and its next backup puts it straight back, which the
   * confirm says rather than leaving the user to notice their storage refill.
   */
  private async chooseScope(items: DeletableItem[]): Promise<CloudDeleteScope | null> {
    const local = await this.presentLocally(items);
    if (!local.length) {
      return (await this.confirmDelete(items, CloudDeleteScope.CloudOnly))
        ? CloudDeleteScope.CloudOnly
        : null;
    }
    const sheet = await this.sheetCtrl.create({
      header: DeleteZoneMessage.CloudDeleteHeader,
      buttons: [
        {
          text: DeleteZoneMessage.CloudOnlyOption,
          role: 'destructive',
          data: CloudDeleteScope.CloudOnly,
        },
        {
          text: DeleteZoneMessage.EverywhereOption,
          role: 'destructive',
          data: CloudDeleteScope.Everywhere,
        },
        { text: DeleteZoneMessage.CancelButton, role: 'cancel' },
      ],
    });
    await sheet.present();
    const picked = (await sheet.onDidDismiss<CloudDeleteScope>()).data;
    if (!picked) return null;
    return (await this.confirmDelete(items, picked)) ? picked : null;
  }

  /** Which of these the device has a row for — what "and from this device"
   *  would actually act on. */
  private async presentLocally(items: DeletableItem[]): Promise<DeletableItem[]> {
    const out: DeletableItem[] = [];
    for (const item of items) {
      try {
        if (await this.db.hasItem(item.kind, item.id)) out.push(item);
      } catch (err) {
        // Treat an unreadable row as absent: offering to delete something we
        // cannot see is a promise we cannot keep.
        this.logger.error('Could not check whether an item is on this device', err, {
          itemId: item.id,
          kind: item.kind,
        });
      }
    }
    return out;
  }

  protected restoreOne(item: DeletableItem): Promise<void> {
    return this.restoreFromCloud([item]);
  }

  protected async restoreMany(items: DeletableItem[]): Promise<void> {
    if (!items.length) return;
    await this.restoreFromCloud(items);
    this.list()?.exitSelectMode();
    this.selection.set([]);
  }

  /**
   * Pull a selection back onto this device. Held to the offline budget first —
   * a restore downloads every file the selection needs, exactly as a Download
   * does — and then confirmed every time: it overwrites the device copy, which
   * is the opposite of what "restore" suggests to someone who has since edited
   * that item here.
   */
  private async restoreFromCloud(items: DeletableItem[]): Promise<void> {
    if (!(await this.fitsOfflineBudget(items))) return;
    const count = items.length;
    const noun = count === 1 ? `"${items[0].name || 'This item'}"` : `${count} items`;
    const ok = await this.confirm(
      SyncMessage.RestoreItemsHeader,
      `${noun} will be copied onto this device. ${SyncMessage.RestoreItemsMessage}`,
      SyncMessage.RestoreItemsButton,
    );
    if (!ok) return;
    if (!(await confirmMeteredTransfer({ alertCtrl: this.alertCtrl, network: this.network }, 'restore'))) {
      return;
    }
    const loading = await this.presentLoading(LoadingMessage.Restoring);
    try {
      const { restored, failed } = await this.sync.restoreCloudItems(
        items.map((i) => ({ kind: i.kind, id: i.id })),
      );
      if (restored) await this.countAgainstOfflineBudget(items);
      await this.toast(
        failed
          ? `${SyncMessage.RestoreItemsComplete} (${restored}) — ${failed} failed`
          : `${SyncMessage.RestoreItemsComplete} (${restored})`,
        failed ? 'warning' : 'medium',
      );
    } catch (err) {
      this.logger.error('Restoring Cloud items to this device failed', err);
      await this.toast(SyncMessage.RestoreItemsFailedPrefix, 'danger');
    } finally {
      await loading.dismiss();
    }
  }

  /**
   * Whether this selection fits in what is left of the offline budget, asked
   * BEFORE the confirm so nobody agrees to a restore only to be told no.
   *
   * Priced with the server's whole-closure figure (`totalBytes`): what could
   * land, not what might already be here. Refusing slightly early is
   * recoverable; overrunning the cap is not.
   */
  private async fitsOfflineBudget(items: DeletableItem[]): Promise<boolean> {
    let verdict: Awaited<ReturnType<ItemDownloadService['checkAllowanceForMany']>>;
    try {
      verdict = await this.downloads.checkAllowanceForMany(
        items.map((i) => ({ id: i.id, bytes: i.totalBytes ?? 0 })),
      );
    } catch (err) {
      // The ledger is a local table; failing to read it is a bug to find, not a
      // reason to keep someone from their own content.
      this.logger.error('Could not check the offline budget before a restore', err);
      return true;
    }
    if (!verdict.refusal) return true;
    const alert = await this.alertCtrl.create({
      header: DownloadCopy.CapHeader,
      message:
        `${DownloadCopy.CapRestoreBody} ${offlineCapSentence(verdict.maxBytes)} ` +
        `You are using ${formatBytes(verdict.bytes)}, and the restore needs ` +
        `${formatBytes(verdict.neededBytes)} more. ${DownloadCopy.CapFreeAndRetry}`,
      buttons: [
        { text: DownloadCopy.Cancel, role: 'cancel' },
        { text: DownloadCopy.CapManage, handler: () => void this.openStorageManager() },
      ],
    });
    await alert.present();
    return false;
  }

  /** "Manage downloads" from inside the Cloud backup sheet: the sheet is a
   *  modal, so it closes first or the storage screen would open behind it. */
  private async openStorageManager(): Promise<void> {
    try {
      await this.modalCtrl.dismiss();
    } catch {
      /* not inside a modal — nothing to close */
    }
    await this.nav.navigateForward(CommonRoute.STORAGE_MANAGER);
  }

  /**
   * What a restore brought down now occupies this device, so it counts against
   * the offline budget exactly as a Download does — otherwise restoring was a
   * way round the cap. Best-effort: the restore itself already succeeded.
   */
  private async countAgainstOfflineBudget(items: DeletableItem[]): Promise<void> {
    for (const item of items) {
      try {
        await this.downloads.recordDownloaded(item.kind, item.id);
      } catch (err) {
        this.logger.warn('Could not count a restored item against the offline budget', err, {
          itemId: item.id,
          kind: item.kind,
        });
      }
    }
    // The card badges read residency from here; without it they would go on
    // showing a cloud over media that has just landed.
    await this.offlineLibrary.refresh();
  }

  /**
   * One batched request, then an honest account of it. Every item is attempted
   * server-side, so a refusal in the middle no longer abandons the rest — and
   * the toast names what happened to each group rather than a flat "failed".
   */
  private async removeFromCloud(
    items: DeletableItem[],
    scope: CloudDeleteScope,
  ): Promise<void> {
    const loading = await this.presentLoading(LoadingMessage.Deleting);
    try {
      if (scope === CloudDeleteScope.Everywhere) {
        // One call per item, and it owns both halves: the local row goes now and
        // the Cloud delete is queued, so an offline device still obeys.
        for (const item of items) await this.sync.permanentlyDelete(item.kind, item.id);
        await this.toast(DeleteZoneMessage.PermanentlyDeleted, 'medium');
        return;
      }
      const results = await this.sync.deleteCloudItems(
        items.map((i) => ({ kind: i.kind, id: i.id })),
      );
      await this.toast(...this.outcomeToast(results));
    } catch (err) {
      this.logger.error('Cloud delete failed', err);
      await this.toast(DeleteZoneMessage.CloudDeleteFailed, 'danger');
    } finally {
      await loading.dismiss();
      // Blocked rows may have become deletable (their parent went with the batch),
      // and every size shifts once shared media loses a referrer.
      await this.reload();
    }
  }

  /** One line covering all three outcomes, leading with what went wrong. */
  private outcomeToast(
    results: Array<{ status: 'deleted' | 'blocked' | 'error' }>,
  ): [string, string] {
    const count = (status: string): number => results.filter((r) => r.status === status).length;
    const deleted = count('deleted');
    const blocked = count('blocked');
    const failed = count('error');
    const deletedPart = `${DeleteZoneMessage.CloudRemoved} (${deleted})`;
    if (!blocked && !failed) return [deletedPart, 'medium'];
    const problems: string[] = [];
    if (blocked) problems.push(`${blocked} still in use`);
    if (failed) problems.push(`${failed} failed`);
    return [`${deletedPart} — ${problems.join(', ')}`, 'warning'];
  }

  /**
   * Two confirms, every time, because neither scope can be undone — and the
   * first message names what survives.
   *
   * Cloud-only keeps saying that the device copy re-uploads on the next backup:
   * it is the consequence users would otherwise discover by watching their
   * storage refill. Everywhere says the opposite plainly — nothing is left.
   */
  private confirmDelete(items: DeletableItem[], scope: CloudDeleteScope): Promise<boolean> {
    const count = items.length;
    const noun = count === 1 ? `"${items[0].name || 'This item'}"` : `${count} items`;
    const frees = items.reduce((sum, i) => sum + (i.exclusiveBytes ?? 0), 0);
    const freesLine = frees > 0 ? ` This frees about ${formatBytes(frees)} in the Cloud.` : '';
    const body =
      scope === CloudDeleteScope.CloudOnly
        ? `${noun} will be permanently deleted from the Cloud.${freesLine} ` +
          `${DeleteZoneMessage.CloudDeleteNoRestore} ${DeleteZoneMessage.CloudDeleteReuploads}`
        : `${noun} will be permanently deleted from the Cloud AND from this device.` +
          `${freesLine} ${DeleteZoneMessage.CloudDeleteNoRestore} ` +
          `${DeleteZoneMessage.EverywhereNothingLeft}`;
    return this.doubleConfirm(
      scope === CloudDeleteScope.CloudOnly
        ? DeleteZoneMessage.CloudDeleteHeader
        : DeleteZoneMessage.EverywhereHeader,
      body,
      DeleteZoneMessage.CloudDeleteSecondConfirm,
    );
  }

  private async doubleConfirm(
    header: string,
    message: string,
    secondMessage: string,
  ): Promise<boolean> {
    const first = await this.confirm(header, message, DeleteZoneMessage.ContinueButton);
    if (!first) return false;
    return this.confirm(
      DeleteZoneMessage.SecondConfirmHeader,
      secondMessage,
      DeleteZoneMessage.DeleteForeverButton,
    );
  }

  private async confirm(
    header: string,
    message: string,
    confirmText: string = DeleteZoneMessage.DeleteButton,
  ): Promise<boolean> {
    const alert = await this.alertCtrl.create({
      header,
      message,
      buttons: [
        { text: DeleteZoneMessage.CancelButton, role: 'cancel' },
        { text: confirmText, role: 'confirm' },
      ],
    });
    await alert.present();
    const { role } = await alert.onDidDismiss();
    return role === 'confirm';
  }

  private presentLoading(message: string) {
    return this.loadingCtrl
      .create({ message, spinner: 'crescent', backdropDismiss: false })
      .then(async (loading) => {
        await loading.present();
        return loading;
      });
  }

  private async toast(message: string, color = 'medium'): Promise<void> {
    const t = await this.toastCtrl.create({
      message,
      color,
      duration: ToastLife.SHORT,
      position: 'bottom',
    });
    await t.present();
  }
}
